import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, List, ListItem, ListItemButton,
  ListItemText, ListItemAvatar, ListItemIcon, Avatar, IconButton, CircularProgress,
  Chip, Collapse, Paper, Divider, Link, Tooltip, Menu, MenuItem,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MailIcon from '@mui/icons-material/Mail';
import DraftsIcon from '@mui/icons-material/Drafts';
import LinkIcon from '@mui/icons-material/Link';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  getEmails, updateEmail, getUserSettings,
  getEmailFolders, createEmailFolder, updateEmailFolder, deleteEmailFolder, moveEmailToFolder,
} from '../services/emails';
import { getDocumentsForItem, getDocumentFolders, updateDocument } from '../services/documents';
import type { Email, EmailFolder } from '../types';
import type { StashDocument, DocumentFolder } from '../types';
import { useUnreadEmail } from '../contexts/UnreadEmailContext';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import MoveDocumentDialog from '../components/documents/MoveDocumentDialog';
import RenameDialog from '../components/documents/RenameDialog';

type EmailView = 'starred' | 'inbound' | 'outbound' | string;

function EmailRow({ email, onUpdate, onRead, folders, emailFolders, onMoveEmail }: {
  email: Email;
  onUpdate: (updated: Partial<Email>) => void;
  onRead: () => void;
  folders: DocumentFolder[];
  emailFolders: EmailFolder[];
  onMoveEmail: (email: Email) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [attachments, setAttachments] = useState<StashDocument[]>([]);
  const [moveDoc, setMoveDoc] = useState<StashDocument | null>(null);
  const navigate = useNavigate();

  const handleClick = async () => {
    const opening = !expanded;
    setExpanded(e => !e);
    if (!email.isRead) {
      await updateEmail(email.id, { isRead: true }).catch(() => {});
      onUpdate({ isRead: true });
      onRead();
    }
    if (opening && email.itemId && attachments.length === 0) {
      getDocumentsForItem(email.itemId).then(setAttachments).catch(() => {});
    }
  };

  const handleStar = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await updateEmail(email.id, { isStarred: !email.isStarred }).catch(() => {});
    onUpdate({ isStarred: !email.isStarred });
  };

  const senderLabel = email.direction === 'inbound'
    ? (email.fromName || email.fromEmail)
    : `To: ${email.toEmail}`;

  const date = new Date(email.createdAt).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <>
      <ListItem
        disablePadding
        secondaryAction={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {emailFolders.length > 0 && (
              <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); onMoveEmail(email); }}>
                <DriveFileMoveIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton edge="end" onClick={handleStar} size="small">
              {email.isStarred ? <StarIcon color="warning" fontSize="small" /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          </Box>
        }
      >
        <ListItemButton onClick={handleClick} sx={{ pr: emailFolders.length > 0 ? 10 : 7 }}>
          <ListItemAvatar>
            <Avatar sx={{ width: 36, height: 36, bgcolor: email.isRead ? 'grey.300' : 'primary.main' }}>
              {email.isRead ? <DraftsIcon fontSize="small" /> : <MailIcon fontSize="small" />}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={email.isRead ? 400 : 700}
                  noWrap
                  sx={{ flex: 1 }}
                >
                  {senderLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {date}
                </Typography>
              </Box>
            }
            secondary={
              <Typography variant="body2" color="text.secondary" noWrap fontWeight={email.isRead ? 400 : 600}>
                {email.subject || '(no subject)'}
              </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Paper variant="outlined" sx={{ mx: 2, mb: 1, p: 2 }}>
          <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {[
              { label: 'From', value: email.fromName ? `${email.fromName} <${email.fromEmail}>` : email.fromEmail },
              { label: 'To', value: email.toEmail },
              ...(email.cc ? [{ label: 'Cc', value: email.cc }] : []),
              { label: 'Subject', value: email.subject || '(no subject)' },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: 'flex' }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 56, flexShrink: 0 }}>{label}</Typography>
                <Typography variant="body2">{value}</Typography>
              </Box>
            ))}
          </Box>
          {attachments.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Attachments</Typography>
              <List dense disablePadding>
                {attachments.map((att) => (
                  <ListItem key={att.id} disablePadding sx={{ gap: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><InsertDriveFileIcon fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary={att.name}
                      secondary={att.fileSize != null ? (att.fileSize < 1024 * 1024 ? `${(att.fileSize / 1024).toFixed(1)} KB` : `${(att.fileSize / (1024 * 1024)).toFixed(1)} MB`) : undefined}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <IconButton size="small" component="a" href={`${import.meta.env.VITE_API_URL || ''}/api/documents/${att.id}/download`} download>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    {att.folderId == null && (
                      <IconButton size="small" onClick={() => setMoveDoc(att)}>
                        <DriveFileMoveIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItem>
                ))}
              </List>
            </>
          )}
          {email.bodyHtml ? (
            <Box
              component="iframe"
              srcDoc={email.bodyHtml}
              sandbox="allow-same-origin"
              sx={{ width: '100%', border: 0, minHeight: 100, display: 'block', mb: email.item ? 2 : 0 }}
              onLoad={(e: React.SyntheticEvent<HTMLIFrameElement>) => {
                const iframe = e.currentTarget;
                if (iframe.contentDocument?.body) {
                  iframe.style.height = iframe.contentDocument.body.scrollHeight + 16 + 'px';
                }
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: email.item ? 2 : 0 }}>
              {email.bodyText || '(no body)'}
            </Typography>
          )}
          {email.item && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                onClick={() => email.itemId && navigate(`/items/${email.itemId}`)}
              >
                <LinkIcon fontSize="small" color="primary" />
                <Chip
                  label={email.item.title || 'View item'}
                  size="small"
                  color="primary"
                  variant="outlined"
                  clickable
                />
              </Box>
            </>
          )}
        </Paper>
      </Collapse>
      {moveDoc && (
        <MoveDocumentDialog
          open
          folders={folders}
          currentFolderId={moveDoc.folderId}
          onClose={() => setMoveDoc(null)}
          onSave={async (folderId) => {
            await updateDocument(moveDoc.id, { folderId });
            setAttachments(prev => prev.map(a => a.id === moveDoc.id ? { ...a, folderId } : a));
            setMoveDoc(null);
          }}
        />
      )}
      <Divider component="li" />
    </>
  );
}

const INBOUND_DOMAIN = 'stash.memespin.com';

export default function EmailsPage() {
  const [view, setView] = useState<EmailView>('all');
  const [emails, setEmails] = useState<Email[]>([]);
  const [emailFolders, setEmailFolders] = useState<EmailFolder[]>([]);
  const [docFolders, setDocFolders] = useState<DocumentFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [inboundHandle, setInboundHandle] = useState<string | null | undefined>(undefined);
  const [folderMenuAnchor, setFolderMenuAnchor] = useState<null | HTMLElement>(null);
  const [folderMenuTarget, setFolderMenuTarget] = useState<EmailFolder | null>(null);
  const [renameFolder, setRenameFolder] = useState<EmailFolder | null>(null);
  const [moveEmailTarget, setMoveEmailTarget] = useState<Email | null>(null);
  const [foldersExpanded, setFoldersExpanded] = useState(() => localStorage.getItem('emails_foldersExpanded') !== 'false');
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('emails_sidebarOpen') !== 'false');
  const { refreshUnreadCount } = useUnreadEmail();

  useEffect(() => {
    getUserSettings().then(s => setInboundHandle(s.inboundHandle ?? null)).catch(() => setInboundHandle(null));
    getDocumentFolders().then(setDocFolders).catch(() => {});
    getEmailFolders().then(setEmailFolders).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const direction = view === 'inbound' ? 'inbound' : view === 'outbound' ? 'outbound' : undefined;
      const systemViews = new Set(['all', 'starred', 'inbound', 'outbound']);
      const opts = !systemViews.has(view) ? { folderId: view } : undefined;
      const data = await getEmails(direction, opts);
      setEmails(data);
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = (id: string, patch: Partial<Email>) => {
    setEmails(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  };

  const handleDeleteFolder = async (folder: EmailFolder) => {
    if (!confirm(`Delete folder "${folder.name}"? Emails will become ungrouped.`)) return;
    await deleteEmailFolder(folder.id);
    setEmailFolders(prev => prev.filter(f => f.id !== folder.id));
    if (view === folder.id) setView('inbound');
  };

  const displayedEmails = view === 'starred' ? emails.filter(e => e.isStarred) : emails;

  const viewTitle =
    view === 'all' ? 'All Emails' :
    view === 'starred' ? 'Starred' :
    view === 'inbound' ? 'Inbox' :
    view === 'outbound' ? 'Sent' :
    emailFolders.find(f => f.id === view)?.name ?? 'Emails';

  const systemViews = [
    { id: 'all', label: 'All Emails' },
    { id: 'starred', label: 'Starred', starred: true },
    { id: 'inbound', label: 'Inbox' },
    { id: 'outbound', label: 'Sent' },
  ];

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left panel */}
      <Box sx={{ width: sidebarOpen ? 220 : 48, flexShrink: 0, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'auto', transition: 'width 0.2s' }}>
        <Box sx={{ p: sidebarOpen ? 1.5 : 0.5, display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'space-between' : 'center' }}>
          <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            <IconButton size="small" onClick={() => setSidebarOpen(prev => { const next = !prev; localStorage.setItem('emails_sidebarOpen', String(next)); return next; })}>
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {sidebarOpen && <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, ml: 1 }}>Folders</Typography>}
          {sidebarOpen && (
            <Tooltip title="New folder">
              <IconButton size="small" onClick={async () => {
                const name = prompt('Folder name:');
                if (!name?.trim()) return;
                const f = await createEmailFolder(name.trim());
                setEmailFolders(prev => [...prev, f].sort((a, b) => a.name.localeCompare(b.name)));
              }}>
                <CreateNewFolderIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <List dense disablePadding>
          {systemViews.map((v) => (
            <ListItem key={v.id} disablePadding>
              <Tooltip title={sidebarOpen ? '' : v.label} placement="right">
                <ListItemButton selected={view === v.id} onClick={() => setView(v.id as EmailView)} sx={{ borderRadius: 1, mx: 0.5, justifyContent: sidebarOpen ? 'initial' : 'center' }}>
                  <ListItemIcon sx={{ minWidth: sidebarOpen ? 32 : 'auto' }}>
                    {v.starred
                      ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                      : view === v.id ? <FolderOpenIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary={v.label} primaryTypographyProps={{ variant: 'body2' }} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
        {sidebarOpen && emailFolders.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <ListItemButton
              onClick={() => setFoldersExpanded(prev => { const next = !prev; localStorage.setItem('emails_foldersExpanded', String(next)); return next; })}
              sx={{ py: 0.5, px: 1.5 }}
            >
              <ListItemText primary="My Folders" primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }} />
              {foldersExpanded ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            </ListItemButton>
            <Collapse in={foldersExpanded} timeout="auto" unmountOnExit>
              <List dense disablePadding>
                {emailFolders.map((f) => (
                  <ListItem key={f.id} disablePadding secondaryAction={
                    <IconButton size="small" edge="end" onClick={(e) => { setFolderMenuAnchor(e.currentTarget); setFolderMenuTarget(f); }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  }>
                    <ListItemButton selected={view === f.id} onClick={() => setView(f.id)} sx={{ borderRadius: 1, mx: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {view === f.id ? <FolderOpenIcon fontSize="small" color="primary" /> : <FolderIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText primary={f.name} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </Box>

      {/* Right panel */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{viewTitle}</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : displayedEmails.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center', px: 2 }}>
            {view === 'all' ? 'No emails yet.' :
             view === 'starred' ? 'No starred emails.' :
             view === 'outbound' ? 'No sent emails yet.' :
             view === 'inbound' && !inboundHandle ? (
              <>
                Claim your{' '}
                <Link component={RouterLink} to="/settings#inbound-handle">stash address</Link>
                {' '}to add items via email.
              </>
            ) : view === 'inbound' ? (
              <>Your stash address is <strong>{inboundHandle}@{INBOUND_DOMAIN}</strong>. No emails yet.</>
            ) : 'No emails here.'}
          </Typography>
        ) : (
          <List disablePadding>
            {displayedEmails.map(email => (
              <EmailRow
                key={email.id}
                email={email}
                folders={docFolders}
                emailFolders={emailFolders}
                onUpdate={(patch) => handleUpdate(email.id, patch)}
                onRead={refreshUnreadCount}
                onMoveEmail={setMoveEmailTarget}
              />
            ))}
          </List>
        )}
      </Box>

      {/* Folder context menu */}
      <Menu anchorEl={folderMenuAnchor} open={Boolean(folderMenuAnchor)} onClose={() => setFolderMenuAnchor(null)}>
        <MenuItem onClick={() => { setRenameFolder(folderMenuTarget); setFolderMenuAnchor(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Rename
        </MenuItem>
        <MenuItem onClick={() => { if (folderMenuTarget) handleDeleteFolder(folderMenuTarget); setFolderMenuAnchor(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Delete
        </MenuItem>
      </Menu>

      {/* Move email to folder menu */}
      <Menu anchorEl={moveEmailTarget ? document.body : null} open={Boolean(moveEmailTarget)} onClose={() => setMoveEmailTarget(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        transformOrigin={{ vertical: 'center', horizontal: 'center' }}>
        <MenuItem disabled><Typography variant="caption">Move to folder</Typography></MenuItem>
        <MenuItem onClick={() => {
          if (moveEmailTarget) moveEmailToFolder(moveEmailTarget.id, null).then(load);
          setMoveEmailTarget(null);
        }}>
          <ListItemIcon><FolderIcon fontSize="small" /></ListItemIcon>Remove from folder
        </MenuItem>
        {emailFolders.map(f => (
          <MenuItem key={f.id} onClick={() => {
            if (moveEmailTarget) moveEmailToFolder(moveEmailTarget.id, f.id).then(load);
            setMoveEmailTarget(null);
          }}>
            <ListItemIcon><FolderIcon fontSize="small" /></ListItemIcon>{f.name}
          </MenuItem>
        ))}
      </Menu>

      {renameFolder && (
        <RenameDialog
          open
          title="Rename folder"
          currentName={renameFolder.name}
          onClose={() => setRenameFolder(null)}
          onSave={async (name) => {
            await updateEmailFolder(renameFolder.id, name);
            setRenameFolder(null);
            getEmailFolders().then(setEmailFolders).catch(() => {});
          }}
        />
      )}
    </Box>
  );
}
